import { Fragment, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Pagination,
} from '@mui/material';
import { Box, Stack, useTheme } from '@mui/system';

import { paths } from 'src/routes/paths';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { useGetLinkedContactsQuery } from 'src/services/candidates/candidates.service';

import EmptyContent from 'src/components/empty-content';
import Iconify from 'src/components/iconify';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import Label from 'src/components/label';
import { LoadingCardItem } from 'src/sections/admin/billing/payment/payment-card-item';

// ----------------------------------------------------------------------

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 5,
};

type Props = {
  candidateId: string;
};

export function LinkedContactsCard({ candidateId }: Props) {
  const { t } = useTranslate();
  const theme = useTheme();

  const [pageOptions, setPageOptions] = useState(DEFAULT_PAGE);

  const { data: linkedContactsData, isLoading: isLoadingLinkedContacts } = useGetLinkedContactsQuery({
    candidate_id: candidateId,
    page: pageOptions.page,
    per_page: pageOptions.per_page,
  }, {
    skip: !candidateId,
  });

  const onChangePage = useCallback((event: React.ChangeEvent<unknown>, pageNumber: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page: pageNumber
    }));
  }, []);

  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
            <Iconify icon="material-symbols:person-outline" />
            <Box>{t('contacts.linked_contacts.title')}</Box>
          </Stack>
        }
      />
      <CardContent sx={{ mb: 2 }}>
        {isLoadingLinkedContacts && (
          <Stack sx={{ width: '100%' }} spacing={2} justifyContent="center" alignItems="center">
            <LoadingCardItem />
            <LoadingCardItem />
            <LoadingCardItem />
          </Stack>
        )}

        {(!isLoadingLinkedContacts && !linkedContactsData?.results?.length) && (
          <Stack sx={{ width: '100%' }} spacing={2} justifyContent="center" alignItems="center">
            <EmptyContent
              title={t('contacts.linked_contacts.empty')}
              description={t('contacts.linked_contacts.empty_description')}
              sx={{ width: '100%' }}
            />
          </Stack>
        )}

        {(!isLoadingLinkedContacts && !!linkedContactsData?.results?.length) && (
          <Stack direction="column" gap={2} justifyContent="start" alignItems="start" sx={{ width: '100%' }}>
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
              {linkedContactsData.results.map((contact) => (
                <Fragment key={contact.id}>
                  <ListItem sx={{ pt: 1 }}>
                    <ListItemAvatar>
                      <Avatar>
                        {contact?.first_name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      sx={{ pr: 4, flexGrow: 1 }}
                      primary={
                        <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
                          <Link
                            to={paths.dashboard.contacts.detail(contact.id)}
                            style={{
                              textDecoration: 'none',
                              color: theme.palette.text.primary,
                              cursor: 'pointer',
                            }}
                          >
                            <TruncatedTextField
                              text={`${contact?.first_name || ''} ${contact?.last_name || ''}`.trim()}
                              mode="characters"
                              limit={30}
                              showMoreEnabled={false}
                              variant="subtitle2"
                              sx={{
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            />
                          </Link>
                        </Stack>
                      }
                      secondary={contact.email}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <ListItemButton sx={{ justifyContent: 'end', p: 0, minWidth: 'fit-content' }}>
                      <Label variant="outlined" sx={{ fontSize: 12 }}>
                        {t(`enums.contact_type.${contact.contact_type}`)}
                      </Label>
                    </ListItemButton>
                  </ListItem>

                  <Divider variant="middle" component="li" />
                </Fragment>
              ))}
            </List>

            <Stack direction="row" justifyContent="center" alignItems="center" sx={{ width: '100%', mt: 2 }}>
              <Pagination
                count={linkedContactsData?.count ? Math.ceil(linkedContactsData.count / pageOptions.per_page) : 1}
                page={pageOptions.page}
                onChange={onChangePage}
                size="small"
              />
            </Stack>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}